import { type LocalPhotoRef, PHOTO_CONTEXTS, type PhotoContext } from '@/shared/capture/capture-session';

export interface PhotoSlotDefinition {
  id: string;
  label: string;
  context: PhotoContext;
  isRequired: boolean;
  registerId?: string;
}

export const INVENTORIZATION_PHOTO_SLOT_IDS = {
  context: 'context',
  detail: 'detail',
  module: 'module',
  registerMain: 'register-main',
} as const;

export const inventorizationPhotoSlots: PhotoSlotDefinition[] = [
  {
    id: INVENTORIZATION_PHOTO_SLOT_IDS.context,
    label: 'Context photos',
    context: PHOTO_CONTEXTS.context,
    isRequired: true,
  },
  {
    id: INVENTORIZATION_PHOTO_SLOT_IDS.detail,
    label: 'Detail photos',
    context: PHOTO_CONTEXTS.detail,
    isRequired: true,
  },
  {
    id: INVENTORIZATION_PHOTO_SLOT_IDS.registerMain,
    label: 'Take photos of counters for all registers you want to create for this meter',
    context: PHOTO_CONTEXTS.register,
    isRequired: true,
  },
  {
    id: INVENTORIZATION_PHOTO_SLOT_IDS.module,
    label: 'Module photos',
    context: PHOTO_CONTEXTS.module,
    isRequired: false,
  },
];

export function readInventorizationPhotoSlot(id: string | undefined) {
  return inventorizationPhotoSlots.find(candidate => candidate.id === id) ?? null;
}

export function getInventorizationPhotoSlot(id: PhotoSlotDefinition['id']) {
  const slot = readInventorizationPhotoSlot(id);

  if (!slot) {
    throw new Error(`Photo slot ${id} is not configured.`);
  }

  return slot;
}

export function matchesInventorizationPhotoSlot(photo: LocalPhotoRef, slot: PhotoSlotDefinition) {
  return (
    photo.context === slot.context && (slot.context !== PHOTO_CONTEXTS.register || matchesRegisterSlot(photo, slot))
  );
}

export function getInventorizationPhotosForSlot(photos: LocalPhotoRef[], slot: PhotoSlotDefinition) {
  return photos
    .filter(photo => matchesInventorizationPhotoSlot(photo, slot))
    .toSorted((left, right) => left.capturedAt.localeCompare(right.capturedAt));
}

function matchesRegisterSlot(photo: LocalPhotoRef, slot: PhotoSlotDefinition) {
  return slot.registerId ? photo.registerId === slot.registerId : true;
}
